import { useCallback, useMemo, useState } from "react"
import { InspectorPanelKeyValueDropdown } from "../InspectorPanelKeyValueDropdown"
import {
    GeometryType,
    formattedDistanceUnit,
    formattedAreaUnit,
    AreaUnit,
    DistanceUnit,
} from "@nova/constants"
import { InspectorPanelSettingsFields } from "../InspectorPanelSettingsFields"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectRulerTemplate, updateRulerTemplate } from "plugins/Toolbox/tool-registry"
import {
    OptimisticLocalStateParams,
    useDefinedOptimisticLocalState,
} from "../useOptimisticLocalState"
import {
    SecondaryFlyoutMenu,
    SecondaryFlyoutMenuContentItem,
} from "../icons/TODO-put-in-package/SecondaryFlyoutMenu"
import { rulerAreaOptions, rulerDistanceOptions } from "./constants"
import {
    selectRulerAreaUnitById,
    selectRulerById,
    selectRulerDistanceUnitById,
} from "features/entity-caching/rulers/selectors"
import { deleteRuler, updateRuler } from "features/pubsub/entityActions/cubes/ruler"
import { ButtonSecondary } from "@nova/component-btn"
import {
    selectWorkspaceAreaUnit,
    selectWorkspaceDistanceUnit,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { createLine } from "features/pubsub/entityActions/cubes/line"
import { omit } from "nodash"
import { Selector } from "react-redux"
import { RootState } from "integrations/redux"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { geometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { clearEditingData } from "plugins/Toolbox/Ruler/slice"

function useInspectorPanelRulerLayer({
    distanceUnitParams,
    areaUnitParams,
}: {
    distanceUnitParams: OptimisticLocalStateParams<DistanceUnit>
    areaUnitParams: OptimisticLocalStateParams<AreaUnit>
}) {
    const [openDropdownId, setOpenDropdownId] = useState<string>()
    const [distanceUnit, setDistanceUnit] = useDefinedOptimisticLocalState(distanceUnitParams)
    const [areaUnit, setAreaUnit] = useDefinedOptimisticLocalState(areaUnitParams)
    return {
        openDropdownId,
        setOpenDropdownId,
        distanceUnit,
        setDistanceUnit,
        areaUnit,
        setAreaUnit,
    }
}

export const InspectorPanelRulerLayerNoTopBorder = ({ id }: { id?: string }) => {
    return <InspectorPanelRulerLayer id={id} shouldShowTopBorder={false} />
}

export const InspectorPanelRulerLayer = ({
    id,
    shouldShowTopBorder,
}: {
    id?: string
    shouldShowTopBorder?: boolean
}) => {
    const dispatch = useAppDispatch()
    const projectId = useAppSelector(selectActivatedFireId)
    const { isEditor } = useWorkspacePermissions(projectId)
    const ruler = useAppSelector(selectRulerById(id || ""))
    const rulerTemplate = useAppSelector(selectRulerTemplate)

    const workspaceDistanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const workspaceAreaUnit = useAppSelector(selectWorkspaceAreaUnit)

    const hookParams: Parameters<typeof useInspectorPanelRulerLayer>[0] = useMemo(
        () =>
            !!id
                ? {
                      distanceUnitParams: {
                          selector: selectRulerDistanceUnitById(id) as Selector<
                              RootState,
                              DistanceUnit
                          >,
                          updateFn: (distanceUnit) => {
                              dispatch(updateRuler({ id, updates: { distanceUnit } }))
                              dispatch(updateRulerTemplate({ distanceUnit }))
                          },
                          defaultValue:
                              (ruler.distanceUnit as DistanceUnit) || workspaceDistanceUnit,
                      },
                      areaUnitParams: {
                          selector: selectRulerAreaUnitById(id) as Selector<RootState, AreaUnit>,
                          updateFn: (areaUnit) => {
                              dispatch(updateRuler({ id, updates: { areaUnit } }))
                              dispatch(updateRulerTemplate({ areaUnit }))
                          },
                          defaultValue: (ruler.areaUnit as AreaUnit) || workspaceAreaUnit,
                      },
                  }
                : {
                      distanceUnitParams: {
                          selector: () => rulerTemplate.distanceUnit || workspaceDistanceUnit,
                          updateFn: (distanceUnit) => {
                              dispatch(updateRulerTemplate({ distanceUnit }))
                          },
                          defaultValue: rulerTemplate.distanceUnit || workspaceDistanceUnit,
                      },
                      areaUnitParams: {
                          selector: () => rulerTemplate.areaUnit || workspaceAreaUnit,
                          updateFn: (areaUnit) => {
                              dispatch(updateRulerTemplate({ areaUnit }))
                          },
                          defaultValue: rulerTemplate.areaUnit || workspaceAreaUnit,
                      },
                  },
        [
            dispatch,
            id,
            ruler?.areaUnit,
            ruler?.distanceUnit,
            rulerTemplate.areaUnit,
            rulerTemplate.distanceUnit,
            workspaceAreaUnit,
            workspaceDistanceUnit,
        ]
    )

    const {
        openDropdownId,
        setOpenDropdownId,
        distanceUnit,
        setDistanceUnit,
        areaUnit,
        setAreaUnit,
    } = useInspectorPanelRulerLayer(hookParams)

    const distanceFlyoutContentItems: SecondaryFlyoutMenuContentItem[] = useMemo(
        () =>
            rulerDistanceOptions.map((option) => ({
                id: option,
                title: formattedDistanceUnit[option].full,
                onClick: () => setDistanceUnit(option),
                isSelected: option === distanceUnit,
            })),
        [distanceUnit, setDistanceUnit]
    )

    const areaFlyoutContentItems: SecondaryFlyoutMenuContentItem[] = useMemo(
        () =>
            rulerAreaOptions.map((option) => ({
                id: option,
                title: formattedAreaUnit[option].full,
                onClick: () => setAreaUnit(option),
                isSelected: option === areaUnit,
            })),
        [areaUnit, setAreaUnit]
    )

    const convertToLine = useCallback(() => {
        dispatch(deleteRuler({ id: ruler.id }))
        dispatch(
            createLine({
                data: {
                    ...omit(ruler, ["id", "index"]),
                    holes: [],
                },
                context: { name: ruler.name, index: ruler.index },
            })
        )
    }, [dispatch, ruler])

    const geometryType = ruler?.geometryType || GeometryType.Line
    useEffectUnsafe(() => {
        return () => {
            geometryDataStore.free(false)
            dispatch(clearEditingData())
        }
    }, [])

    return (
        <InspectorPanelSettingsFields shouldShowTopBorder={shouldShowTopBorder}>
            {/* Distance Unit */}
            <InspectorPanelKeyValueDropdown
                id="distance-unit"
                isOpen={openDropdownId === "distance-unit"}
                setOpenDropdownId={setOpenDropdownId}
                keyTitle="Distance Unit"
                dropdownTitle={formattedDistanceUnit[distanceUnit].abbrev}
            >
                <SecondaryFlyoutMenu
                    className="whitespace-nowrap"
                    items={distanceFlyoutContentItems}
                    onBlur={() => {
                        setOpenDropdownId(undefined)
                    }}
                />
            </InspectorPanelKeyValueDropdown>

            {/* Area Unit */}
            {(geometryType === GeometryType.Polygon || !id) && (
                <InspectorPanelKeyValueDropdown
                    id="area-unit"
                    isOpen={openDropdownId === "area-unit"}
                    setOpenDropdownId={setOpenDropdownId}
                    keyTitle="Area Unit"
                    dropdownTitle={formattedAreaUnit[areaUnit].abbrev}
                >
                    <SecondaryFlyoutMenu
                        className="whitespace-nowrap"
                        items={areaFlyoutContentItems}
                        onBlur={() => {
                            setOpenDropdownId(undefined)
                        }}
                    />
                </InspectorPanelKeyValueDropdown>
            )}

            {/* Convert to Line Button */}
            {isEditor && !!id && (
                <ButtonSecondary
                    LeftIcon={Icons.DesignServices}
                    title={`Convert to ${geometryType === GeometryType.Line ? "Line" : "Polygon"}`}
                    className="mt-2"
                    onClick={convertToLine}
                />
            )}
        </InspectorPanelSettingsFields>
    )
}
